import {
    Fragment,
    useState,
    useMemo,
    type FC,
    useId
} from "react";
import {
    type LayoutChangeEvent,
    Animated,
    Platform,
    View
} from "react-native";
import type ISkeletonShapeProps from "./type";
import stylesheet, {
    getSkeletonShapeColors,
    getSkeletonAnimation,
    getSkeletonBandRange,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useReducedMotion
} from "../../../../helpers/motion";
import {
    useSkeletonClock
} from "../../clock";
import {
    LinearGradient,
    Defs,
    Rect,
    Stop,
    Svg
} from "react-native-svg";

const SkeletonShape: FC<ISkeletonShapeProps> = ({
    onLayout: onLayoutProp,
    customTheme,
    animation,
    style,
    ...props
}) => {
    const {
        durations,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const isReduceMotionEnabled = useReducedMotion();

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const gradientID = useId().replace(/[^a-zA-Z0-9]/g, "");

    const isWeb = Platform.OS === "web";

    const [
        width,
        setWidth
    ] = useState<number>(0);

    const loopDuration = durations.loop;

    const phaseDelay = useMemo(() => loopDuration ? -(Date.now() % loopDuration) : 0, [
        loopDuration
    ]);

    const currentAnimation = getSkeletonAnimation({
        isReduceMotionEnabled,
        loopDuration,
        animation
    });

    const clockValue = useSkeletonClock({
        kind: isWeb ? "none" : currentAnimation,
        duration: loopDuration
    });

    const currentColors = getSkeletonShapeColors({
        colors
    });

    const {
        webAnimation: webAnimationDynamicStyle,
        baseLayer: baseLayerDynamicStyle,
        peakLayer: peakLayerDynamicStyle,
        webBand: webBandDynamicStyle,
        shape: shapeDynamicStyle,
        band: bandDynamicStyle
    } = useStyles({
        animation: currentAnimation,
        bandWidth: width,
        currentColors,
        loopDuration,
        phaseDelay
    });

    const onLayout = (event: LayoutChangeEvent) => {
        setWidth(event.nativeEvent.layout.width);

        onLayoutProp?.(event);
    };

    const renderPulse = () => {
        if(currentAnimation !== "pulse") {
            return null;
        }

        if(isWeb) {
            return <Fragment>
                <View
                    style={[
                        stylesheet.layer,
                        baseLayerDynamicStyle,
                        stylesheet.pulseBase,
                        webAnimationDynamicStyle
                    ]}
                />
                <View
                    style={[
                        stylesheet.layer,
                        peakLayerDynamicStyle,
                        stylesheet.pulsePeak,
                        webAnimationDynamicStyle
                    ]}
                />
            </Fragment>;
        }

        if(!clockValue) {
            return null;
        }

        return <Fragment>
            <Animated.View
                style={[
                    stylesheet.layer,
                    baseLayerDynamicStyle,
                    {
                        opacity: clockValue.interpolate({
                            outputRange: [
                                1,
                                0
                            ],
                            inputRange: [
                                0,
                                1
                            ]
                        })
                    }
                ]}
            />
            <Animated.View
                style={[
                    stylesheet.layer,
                    peakLayerDynamicStyle,
                    {
                        opacity: clockValue
                    }
                ]}
            />
        </Fragment>;
    };

    const renderShimmer = () => {
        if(currentAnimation !== "shimmer") {
            return null;
        }

        if(isWeb) {
            return <View
                style={[
                    stylesheet.band,
                    webBandDynamicStyle,
                    direction === "rtl" ? stylesheet.shimmerEnd : stylesheet.shimmerStart,
                    webAnimationDynamicStyle
                ]}
            />;
        }

        if(!clockValue || !width) {
            return null;
        }

        return <Animated.View
            style={[
                stylesheet.band,
                bandDynamicStyle,
                {
                    transform: [
                        {
                            translateX: clockValue.interpolate({
                                outputRange: getSkeletonBandRange({
                                    direction,
                                    width
                                }),
                                inputRange: [
                                    0,
                                    1
                                ]
                            })
                        }
                    ]
                }
            ]}
        >
            <Svg
                height="100%"
                width="100%"
            >
                <Defs>
                    <LinearGradient
                        id={gradientID}
                        x1="0"
                        x2="1"
                        y1="0"
                        y2="0"
                    >
                        <Stop
                            stopColor={currentColors.clearColor}
                            offset="0"
                        />
                        <Stop
                            stopColor={currentColors.peakColor}
                            offset="0.5"
                        />
                        <Stop
                            stopColor={currentColors.clearColor}
                            offset="1"
                        />
                    </LinearGradient>
                </Defs>
                <Rect
                    fill={`url(#${gradientID})`}
                    height="100%"
                    width="100%"
                />
            </Svg>
        </Animated.View>;
    };

    return <View
        {...props}
        onLayout={isWeb ? onLayoutProp : onLayout}
        style={[
            style,
            stylesheet.shape,
            shapeDynamicStyle
        ]}
    >
        {renderPulse()}
        {renderShimmer()}
    </View>;
};
export default SkeletonShape;
